import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, MessageSquare, Eye, Edit, Trash2, Loader2, FileDown } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { EmployeeConversationForm } from "./EmployeeConversationForm";
import { EmployeeConversationView } from "./EmployeeConversationView";
import { exportConversationToPDF } from "@/lib/conversationPdfExport";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "./ui/alert-dialog";

interface EmployeeConversationsTabProps {
  employeeId: string;
  employeeName: string;
  readOnly?: boolean;
}

interface Conversation {
  id: string;
  employee_id: string;
  conversation_type: "jahresgespraech" | "followup" | "sonstiges";
  conversation_date: string;
  follow_up_date: string | null;
  status: "entwurf" | "abgeschlossen";
  created_at: string;
  created_by: string | null;
}

const conversationTypeLabels: Record<string, string> = {
  jahresgespraech: "Jahresgespräch",
  followup: "Follow-up-Gespräch",
  sonstiges: "Sonstiges",
};

const statusLabels: Record<string, string> = {
  entwurf: "Entwurf",
  abgeschlossen: "Abgeschlossen",
};

export function EmployeeConversationsTab({ 
  employeeId, 
  employeeName,
  readOnly = false 
}: EmployeeConversationsTabProps) {
  const { user, userRole } = useAuth();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showView, setShowView] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);

  const isAdmin = userRole === "admin";
  const canManage = userRole === "admin" || userRole === "hr_manager";

  useEffect(() => {
    loadConversations();
  }, [employeeId]);

  const loadConversations = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("employee_conversations")
        .select("id, employee_id, conversation_type, conversation_date, follow_up_date, status, created_at, created_by")
        .eq("employee_id", employeeId)
        .order("conversation_date", { ascending: false });

      if (error) throw error;
      setConversations(data || []);
    } catch (error: any) {
      console.error("Error loading conversations:", error);
      toast.error("Fehler beim Laden der Gespräche");
    } finally {
      setLoading(false);
    }
  };

  const handleNewConversation = () => {
    setSelectedConversation(null);
    setEditMode(false);
    setShowForm(true);
  };

  const handleEditConversation = (id: string) => {
    setSelectedConversation(id);
    setEditMode(true);
    setShowForm(true);
  };

  const handleViewConversation = (id: string) => {
    setSelectedConversation(id);
    setShowView(true);
  };

  const handleDeleteConversation = async (id: string) => {
    try {
      const { error } = await supabase
        .from("employee_conversations")
        .delete()
        .eq("id", id);

      if (error) throw error;
      toast.success("Gespräch erfolgreich gelöscht");
      loadConversations();
    } catch (error: any) {
      console.error("Error deleting conversation:", error);
      toast.error("Fehler beim Löschen des Gesprächs");
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setSelectedConversation(null);
    setEditMode(false);
    loadConversations();
  };

  const handleViewClose = () => {
    setShowView(false);
    setSelectedConversation(null);
  };

  const handleExportPDF = async (conversationId: string) => {
    try {
      const { data: conv, error } = await supabase
        .from("employee_conversations")
        .select("*")
        .eq("id", conversationId)
        .single();

      if (error) throw error;
      
      const { data: profile } = await supabase
        .from("profiles")
        .select("full_name")
        .eq("id", user?.id)
        .single();

      await exportConversationToPDF(conv, employeeName, {
        createdByName: profile?.full_name || undefined,
      });
      
      toast.success("PDF erfolgreich erstellt");
    } catch (error: any) {
      console.error("Error exporting PDF:", error);
      toast.error("Fehler beim PDF-Export");
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Mitarbeitergespräche
          </CardTitle>
          {canManage && !readOnly && (
            <Button onClick={handleNewConversation}>
              <Plus className="h-4 w-4 mr-2" />
              Neues Gespräch anlegen
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {conversations.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Keine Mitarbeitergespräche vorhanden</p>
              {canManage && !readOnly && (
                <Button variant="outline" className="mt-4" onClick={handleNewConversation}>
                  <Plus className="h-4 w-4 mr-2" />
                  Erstes Gespräch anlegen
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Datum</TableHead>
                    <TableHead>Gesprächsart</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Follow-up</TableHead>
                    <TableHead className="text-right">Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {conversations.map((conversation) => (
                    <TableRow key={conversation.id}>
                      <TableCell>
                        {format(new Date(conversation.conversation_date), "dd.MM.yyyy", { locale: de })}
                      </TableCell>
                      <TableCell>
                        {conversationTypeLabels[conversation.conversation_type]}
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant={conversation.status === "abgeschlossen" ? "default" : "secondary"}
                          className={conversation.status === "abgeschlossen" ? "bg-green-600" : ""}
                        >
                          {statusLabels[conversation.status]}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {conversation.follow_up_date 
                          ? format(new Date(conversation.follow_up_date), "dd.MM.yyyy", { locale: de })
                          : "-"
                        }
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewConversation(conversation.id)}
                            title="Ansehen"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleExportPDF(conversation.id)}
                            title="Als PDF exportieren"
                          >
                            <FileDown className="h-4 w-4" />
                          </Button>
                          {canManage && !readOnly && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditConversation(conversation.id)}
                              title="Bearbeiten"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          )}
                          {isAdmin && !readOnly && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="ghost" size="sm" className="text-destructive" title="Löschen">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Gespräch löschen?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Möchten Sie dieses Mitarbeitergespräch wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleDeleteConversation(conversation.id)}
                                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                  >
                                    Löschen
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editMode ? "Gespräch bearbeiten" : "Neues Mitarbeitergespräch"}
            </DialogTitle>
          </DialogHeader>
          <EmployeeConversationForm
            employeeId={employeeId}
            employeeName={employeeName}
            conversationId={selectedConversation}
            onClose={handleFormClose}
          />
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={showView} onOpenChange={setShowView}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Mitarbeitergespräch ansehen</DialogTitle>
          </DialogHeader>
          {selectedConversation && (
            <EmployeeConversationView
              conversationId={selectedConversation}
              employeeName={employeeName}
              onClose={handleViewClose}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
